import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Typography,
  IconButton,
  makeStyles,
  Theme,
} from '@material-ui/core';
import {ServiceInterval} from '../../rubblemaster_openapi3_typescript-fetch/models/MaintenanceInfoDto'
import CloseIcon from '@material-ui/icons/Close';
import { useTranslation } from 'react-i18next';
import { CircularProgress } from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import { IMachine, IReduxState } from '../../types';
import { useSelector } from 'react-redux';
import i18n from '../../i18n';
import { makeSafeFileName } from '../../helpers/utils';
import ServiceNotificationsTab from './ServiceDetailNotification';
import { themeColors } from '../../constants/theme';
import MachineServiceTab from './ServiceDetailHistory';

export interface IServiceInterval {
  workingHours: number;
  serviceName: string;
  machineTypeId: number;
  engineTypeId: number;
  repetitive: number;
  [key: string]: any;
}

interface IProps {
  open: boolean;
  onClose: () => void;
  serviceIntervals: ServiceInterval[];
  // Betriebsstunden-Schwelle des Eintrags, ab dem die Tabelle beginnen soll
  serviceRuntimeHour: number | null | undefined;
}

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    backgroundColor: '#222',
    color: '#fff',
    borderRadius: 10,
    boxShadow: `0px 0px 10px ${themeColors.yellow}`,
    minWidth: 700,
  },
  tabs: {
    backgroundColor: '#444',
    borderRadius: '10px 10px 0 0',
  },
  tabIndicator: {
    backgroundColor: themeColors.yellow,
    height: 3,
  },
  tabRoot: {
    color: '#fff',
    fontWeight: 500,
  },
  tabSelected: {
    color: themeColors.yellow,
    fontWeight: 700,
  },
  tableHeadCell: {
    backgroundColor: '#555',
    color: '#fff',
    borderColor: themeColors.yellow,
  },
  tableCell: {
    color: '#fff',
    borderColor: themeColors.yellow,
  },
  btn: {
    backgroundColor: themeColors.yellow,
    color: '#000',
    marginRight: theme.spacing(1),
    '&:hover': {
      backgroundColor: '#fff59d',
    },
  },
  closeBtn: {
    color: '#fff',
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
  title: {
    padding: theme.spacing(2, 2, 0, 2),
    position: 'relative',
  },
}));

function TabPanel({
  children,
  value,
  index,
}: {
  children: React.ReactNode;
  value: number;
  index: number;
}) {
  return value === index ? <div style={{ padding: 16 }}>{children}</div> : null;
}

export default function ServiceDetailDialog(props: IProps) {
  const { open, onClose, serviceIntervals, serviceRuntimeHour } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);

  const machine = useSelector((state: IReduxState) => state.machineDetails.machine);

  const machineId = machine.id;
  
  // Findet den Index des Eintrags, dessen workingHours der aktuellen
  // serviceRuntimeHour entspricht, und schneidet die Liste ab dort zu
  const startIndex = serviceIntervals.findIndex(
    (interval) => interval.workingHours === serviceRuntimeHour
  );
  const upcomingServices =
    (startIndex >= 0 ? serviceIntervals.slice(startIndex) : serviceIntervals).slice(0, 6);

  const [loadingProtocol, setLoadingProtocol] = useState(false);

  const handleProtocolDownload = async (interval: ServiceInterval) => {
    const fileAccess = interval.fileAccess;

    if (!fileAccess) {
      console.error('Kein fileAccess-Eintrag für dieses Service-Intervall vorhanden.');
      return;
    }

    console.log("fileAccess:");
    console.log(fileAccess);

    setLoadingProtocol(true);

    try {
      const langId = i18n.language?.toUpperCase().startsWith('DE') ? 'DE' : 'EN';

      const params = new URLSearchParams({
        FileAccess: String(fileAccess.fileAccess),
        DocType: String(fileAccess.docType),
        LocalPath: fileAccess.localPath,
        NameEN: fileAccess.nameEN,
        NameDE: fileAccess.nameDE,
        LangId: langId,
      });

      const response = await fetch(
          `/api/machines/${machineId}/protocol?${params.toString()}`
        );

      if (!response.ok) {
        throw new Error("Download failed");
      }

      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const fileNameBase = langId === 'DE' ? fileAccess.nameDE : fileAccess.nameEN;
      link.download = `${makeSafeFileName(fileNameBase)}.pdf`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingProtocol(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      classes={{ paper: classes.paper }}
    >
      <div className={classes.title}>
        <Typography variant="h6">{t('machine.serviceDetails')}</Typography>
        <IconButton className={classes.closeBtn} onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </div>

      <Tabs
        value={tab}
        onChange={(_, newValue) => setTab(newValue)}
        className={classes.tabs}
        classes={{ indicator: classes.tabIndicator }}
        variant="fullWidth"
      >
        <Tab
          label={t('machine.serviceOverview')}
          classes={{ root: classes.tabRoot, selected: classes.tabSelected }}
        />
        <Tab
          label={t('machine.serviceHistory')}
          classes={{ root: classes.tabRoot, selected: classes.tabSelected }}
        />
        <Tab
          label={t('machine.serviceNotifications')}
          classes={{ root: classes.tabRoot, selected: classes.tabSelected }}
        />
      </Tabs>

      <DialogContent>
        {/* Tab 0: Serviceübersicht */}
        <TabPanel value={tab} index={0}>
          <Typography variant="subtitle1" gutterBottom>
            {t('machine.currentAndUpcomingServices')}
          </Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHeadCell}>
                  {t('machine.workingHours')}
                </TableCell>
                <TableCell className={classes.tableHeadCell}>
                  {t('machine.serviceName')}
                </TableCell>
                <TableCell className={classes.tableHeadCell}>
                  {t('machine.serviceDocuments')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {upcomingServices.map((interval, index) => (
                <TableRow key={`${interval.workingHours}-${index}`}>
                  <TableCell className={classes.tableCell}>
                    {interval.workingHours}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {interval.serviceName}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Button
                      className={classes.btn}
                      size="small"
                      startIcon={
                        loadingProtocol ? (
                          <CircularProgress size={16} />
                        ) : (
                          <GetAppIcon fontSize="small" />
                        )
                      }
                      disabled={loadingProtocol}
                      onClick={() => handleProtocolDownload(interval)}
                    >
                      {t('machine.serviceProtokolDownload')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TabPanel>

        {/* Tab 1: Servicehistorie */}
        <TabPanel value={tab} index={1}>
          <MachineServiceTab />
        </TabPanel>
        
        {/* Tab 2: Benachrichtigungen */}
        <TabPanel value={tab} index={2}>
          <ServiceNotificationsTab />
        </TabPanel>
        
      </DialogContent>
    </Dialog>
  );
}